import React, { useCallback, useMemo } from "react";
import { Button, Spinner, MessageBar, MessageBarBody, MessageBarTitle } from "@fluentui/react-components";
import { ArrowSync16Regular, InfoRegular, PlayCircle16Regular, RecordStop16Regular } from "@fluentui/react-icons";

import { searchAndHighlight } from "@/taskpane/lib";
import { useRiskAnalysisStyles } from "@/pages/risk-analysis/styles";
import { RiskAnalysisTable } from "@/components/risk-analysis-table";
import { useDocumentClassification } from "@/context/document-classification";
import { trackEvent } from "@/services/analytics-service";
import { AnalyticsKey } from "@/types/analytics";
import { useAuth } from "@/context/auth";
import { PromptConfigSection } from "./components/prompt-config-section";
import { PlaybookSection } from "./components/playbook-section";
import GenerateReportBtn from "./components/generate-report-btn";
import { CSVLink } from "react-csv";
import checkUniqueLocalIdSupport from "@/taskpane/lib/utils/checkUniqueLocalIdSupport";
import { DocumentParagraph } from "@/types/document";

export const RiskAnalysis: React.FC = () => {
  const styles = useRiskAnalysisStyles();
  const {
    classifications,
    refreshAnalysis,
    promptsUsed,
    getAnalysisClassification,
    selectedPromptConfig,
    isFetchingPromptConfigs,
    fetchingPlaybooks,
    errors,
    handleCancel,
    currentClassification,
    fetchPlaybooks,
  } = useDocumentClassification();
  const { user } = useAuth();
  const reportData = useMemo(() => {
    if (!currentClassification) return [];
    return currentClassification?.rules
      .map((rule) => {
        if (!rule.analysis) return null;
        return {
          ["Term/Clause Topic"]: rule.clauseTopic,
          Value: rule.paragraphs?.length ? rule.analysis?.clause?.resultStatus?.message || "" : "Not Included",
          Summary: rule.analysis?.clause?.summary?.join("\n")?.replace(/"/g, "'"),
          ["Customer Rule"]: rule.analysis?.clause?.preferredLanguage?.replace(/"/g, "'"),
          ["Full Analysis"]: rule.analysis?.clause?.fullReport?.join("\n")?.replace(/"/g, "'"),
        };
      })
      .filter(Boolean);
  }, [currentClassification]);
  const analysisExists = currentClassification?.rules?.some((rule) => !!rule.analysis);

  const handleHighlight = useCallback(async (paragraph: DocumentParagraph) => {
    if (!paragraph?.id) return;
    await searchAndHighlight(paragraph);
    trackEvent(AnalyticsKey.ADDIN.DOCUMENT.HIGHLIGHT, {
      userId: user.user.id,
      highlightedText: paragraph.text,
    });
  }, []);

  const [unsupportedVersionError, setUnsupportedVersionError] = React.useState(false);

  const handleInitializeAnalysis = async () => {
    try {
      const isWordVersionSupported = await checkUniqueLocalIdSupport();
      if (!isWordVersionSupported) {
        setUnsupportedVersionError(true);
        return false;
      }
    } catch (err) {
      setUnsupportedVersionError(true);
      return false;
    }

    if (selectedPromptConfig) {
      getAnalysisClassification();
    }
    return true;
  };

  const isLoading = fetchingPlaybooks || isFetchingPromptConfigs;
  const hasSomeAnalysisOnLoading = currentClassification?.rules?.some((rule) => rule.isLoading);
  const hasAnalysisFinished = currentClassification?.rules?.every((rule) => !!rule.analysis);

  return (
    <div className={styles.container}>
      <div className={styles.dropdownsContainer}>
        {user?.user?.role === "admin" && <PromptConfigSection />}
        <PlaybookSection />
        <CSVLink data={reportData} filename={"my-file.csv"} className={styles.generateReportBtn} target="_blank">
          <GenerateReportBtn disabled={!analysisExists} />
        </CSVLink>
      </div>
      {isLoading && (
        <div className={styles.loading}>
          <Spinner size="extra-small" />
          <span className={styles.loadingText}>Loading playbook...</span>
        </div>
      )}
      {!isLoading && !unsupportedVersionError && classifications.length === 0 && (
        <div className={styles.loading}>
          <InfoRegular style={{ fontSize: "1.5rem" }} />
          <span className={styles.loadingText}>No playbooks found!</span>
        </div>
      )}
      {!isLoading && unsupportedVersionError && (
        <div className={styles.loading}>
          <InfoRegular style={{ fontSize: "1.5rem" }} />
          <span className={styles.loadingText}>Unsupported version of Word!</span>
        </div>
      )}
      {!isLoading && !unsupportedVersionError && classifications.length > 0 && (
        <>
          {errors.length > 0 && errors.find((error) => error.field === "playbook") ? (
            <div className={styles.errorsContainer}>
              <div className={styles.errorTitle}>Error fetching the playbook</div>
              <Button icon={<ArrowSync16Regular />} onClick={() => fetchPlaybooks()} aria-label="Refresh playbook">
                Refresh Playbook
              </Button>
            </div>
          ) : classifications.length > 0 ? (
            <div className={styles.classificationContainer}>
              <div className={styles.classificationResultsTitleContainer}>
                <span className={styles.classificationResultsTitle}>
                  {hasAnalysisFinished || hasSomeAnalysisOnLoading ? "Analysis Result" : "Playbook Terms"}
                </span>
                {hasAnalysisFinished && (
                  <Button
                    appearance="primary"
                    icon={<ArrowSync16Regular />}
                    iconPosition="after"
                    onClick={() => refreshAnalysis()}
                    aria-label="Refresh analysis"
                  >
                    Refresh
                  </Button>
                )}
                {hasSomeAnalysisOnLoading && (
                  <Button
                    appearance="subtle"
                    iconPosition="after"
                    icon={<RecordStop16Regular />}
                    onClick={handleCancel}
                    aria-label="Stop analysis"
                  >
                    Stop
                  </Button>
                )}
                {!hasAnalysisFinished && !hasSomeAnalysisOnLoading && (
                  <Button
                    appearance="primary"
                    iconPosition="after"
                    icon={<PlayCircle16Regular />}
                    onClick={handleInitializeAnalysis}
                    aria-label="Play analysis"
                  >
                    Run analysis
                  </Button>
                )}
              </div>
              <RiskAnalysisTable
                classification={currentClassification}
                handleHighlight={handleHighlight}
                promptsUsed={promptsUsed.find(
                  (promptUsed) => promptUsed.playbookId === currentClassification.playbookId
                )}
                isLoadingPlaybook={fetchingPlaybooks}
                refreshAnalysis={refreshAnalysis}
              />
            </div>
          ) : (
            <div className={styles.emptyState}>
              <MessageBar layout="multiline" intent="error">
                <MessageBarBody>
                  <MessageBarTitle>No playbook available</MessageBarTitle>
                  Please create a playbook to analyze your document.
                </MessageBarBody>
              </MessageBar>
            </div>
          )}
        </>
      )}
    </div>
  );
};
